import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import React, { useMemo } from 'react'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import LEOfferPriceDetails from './LEOfferPriceDetails'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import LuxPlusHiddenOfferCarouselCard from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferCarouselCard'
import { connect } from 'react-redux'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { isHotelPackage } from 'lib/offer/offerTypes'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
interface MappedProps {
  isLuxPlusEnabled: boolean;
  isLuxPlusHiddenOffer: boolean;
  inclusionsViewableLuxPlusTier: App.MembershipTier;
}

interface Props extends MappedProps {
  offer: App.HotelOffer | App.HotelOfferSummary
  onClick?: () => void
  className?: string;
  offerUrl: string;
  size: 'small' | 'medium';
  selectedDatesAndRoomsFilter?: App.OfferListFilters;
}

function LEOfferCard(props: Props) {
  const {
    offer,
    size,
    onClick,
    className,
    offerUrl,
    isLuxPlusHiddenOffer,
    isLuxPlusEnabled,
    selectedDatesAndRoomsFilter,
    inclusionsViewableLuxPlusTier,
  } = props
  const { lowestPricePackage } = offer
  const isUltraLux = isUltraLuxOffer(offer)
  const memberPrice = lowestPricePackage?.memberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (lowestPricePackage?.price ?? 0) - memberPrice : undefined
  const luxPlusInclusions = lowestPricePackage?.luxPlusInclusionsByTier?.[inclusionsViewableLuxPlusTier as App.MembershipSubscriptionTier]
  const offerLabels = useMemo(() => offer.urgencyTags.filter(label => label.type !== 'left'), [offer])

  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter || {})
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (offer.bundledWithFlightsOnly) {
      return []
    }
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        pkg: isHotelPackage(pkg) ? pkg : undefined,
        price: pkg.price,
        memberPrice: pkg.memberPrice,
      }),
    ]
  }, [offer, pkg])

  let imageCornerLabel = <BookingsCountLabel bookings={offer.stats.booked} threshold="offer" />
  if (isUltraLux) {
    imageCornerLabel = <ProductTypeLabel productType={offer.productType} />
  }

  let urgencyTagLabel: React.ReactElement| null = null
  const urgencyTagAlreadyActive = offerLabels.find((ol:App.OfferUrgencyLabel) => ol.type == 'left' && ol.end || ol.type == 'lux_plus_general_release')
  if (offer?.visibilitySchedule?.end && !urgencyTagAlreadyActive) {
    urgencyTagLabel = <OfferUrgencyCountdownLabel endDate={offer?.visibilitySchedule?.end} tagPlace="offer-card" fallback={imageCornerLabel}/>
  }

  return (
    <ProductPaletteProvider product={offer} >
      {isLuxPlusHiddenOffer && <LuxPlusHiddenOfferCarouselCard urgencyLabels={offerLabels} offer={offer} />}
      {!isLuxPlusHiddenOffer && <>
        {size === 'medium' && <CarouselCardMedium
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
          image={offer.image}
          hoverImage={offer.images[1]}
          location={[offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')}
          providerName={offer.property.name}
          title={offer.name}
          priceDetails={<LEOfferPriceDetails variant="condensed" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} />}
          to={offerUrl}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer}/>}
          urgencyLabels={<LabelGroup>
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
            {urgencyTagLabel}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />}
          onClick={onClick}
        />}
        {size === 'small' && <CarouselCardSmall
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
          description={offer.name}
          image={offer.image}
          hoverImage={offer.images[1]}
          location={[offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')}
          title={offer.property.name}
          priceDetails={<LEOfferPriceDetails offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} />}
          to={offerUrl}
          productType={offer.productType}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer}/>}
          urgencyLabels={<LabelGroup>
            <OfferLabels
              offer={offer}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {urgencyTagLabel}
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />}
          onClick={onClick}
          imageCornerLabel={ imageCornerLabel }
        />}
      </>}
    </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>): MappedProps {
  return ({
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    isLuxPlusHiddenOffer: getIsLuxPlusHiddenOffer(state, ownProps.offer),
    inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(state),
  })
}

export default connect(mapStateToProps)(LEOfferCard)
